import { API_PETTY_CASH_REQUISITION } from "../../../utils/apis";
import { prepareObjectArrayFormdataPost } from "../../../utils/helpers";
import { IFormSection } from "../../../types/common";
import dayjs from "dayjs";

export type PettyCashItem = {
  item_name: string;
  item_description?: string;
  quantity: number | string;
  amount: number | string;
  purpose?: string;
};

const lineTotal = (item: PettyCashItem) =>
  (Number(item.quantity) || 0) * (Number(item.amount) || 0);

export function usePettyCashRequisitionForm(form?: any) {
  const requisitionItems: PettyCashItem[] = form?.requisition_items ?? [];

  const totalAppliedFor = requisitionItems.reduce(
    (sum, item) => sum + lineTotal(item),
    0,
  );

  const sections: IFormSection[] = [
    {
      title: "Petty Cash Requisition Details",
      fields: [
        {
          field_name: "reference_number",
          label: "Reference Number",
          type: "text",
          props: { required: true },
        },
        {
          field_name: "start_date",
          label: "Start Date",
          type: "date",
          props: { required: true },
        },
        {
          field_name: "end_date",
          label: "End Date",
          type: "date",
          props: {
            required: true,
            minDate: form?.start_date ? dayjs(form.start_date) : undefined,
          },
        },
        {
          field_name: "requisition_items",
          label: "Add Requisition Item",
          type: "modal",
          props: { wide: true, required: true },
          fields: [
            {
              field_name: "item_name",
              label: "Item Name",
              type: "text",
              props: { required: true, placeholder: "Enter item name" },
            },
            {
              field_name: "item_description",
              label: "Item Description",
              type: "textarea",
              props: { maxLength: 500, placeholder: "Enter description" },
            },
            {
              field_name: "quantity",
              label: "Quantity",
              type: "number",
              props: { required: true, min: 1 },
            },
            {
              field_name: "amount",
              label: "Unit Amount",
              type: "number",
              props: { required: true, min: 0 },
            },
            {
              field_name: "purpose",
              label: "Purpose",
              type: "textarea",
              props: { maxLength: 1000, placeholder: "Enter purpose" },
            },
          ],
        },
      ],
    },
  ];

  function buildPayload(values: any) {
    const requisition_items = (values?.requisition_items ?? []).map(
      (item: PettyCashItem) => ({
        item_name: item.item_name,
        item_description: item.item_description,
        quantity: Number(item.quantity) || 0,
        amount: Number(item.amount) || 0,
        total_applied_for: lineTotal(item),
        ...(item.purpose && { purpose: item.purpose }),
      }),
    );

    return prepareObjectArrayFormdataPost(
      {
        reference_number: values?.reference_number,
        start_date: values?.start_date,
        end_date: values?.end_date,
        requisition_items,
      },
      "requisition_items",
    );
  }

  function onSubmit(values: any) {
    return {
      api: API_PETTY_CASH_REQUISITION,
      method: "POST",
      params: buildPayload(values),
    };
  }

  const enableSubmit =
    !!form?.reference_number &&
    !!form?.start_date &&
    !!form?.end_date &&
    requisitionItems.length > 0 &&
    requisitionItems.every((item) => item.item_name && item.amount);

  return { sections, onSubmit, enableSubmit, totalAppliedFor };
}
